import * as Request from '../../request'
import * as Common from '../common'
import * as Constants from './constants'
import { TopicPreference, convertApiResponseToTopics } from './actions'
import * as Utils from '../common/utils'
import * as CommonConstants from '../common/constants'

export interface TopicPreferencesState extends Common.SubmittableCommunityState {
  memberGuid: Common.Guid;
  topics: TopicPreference[];
  recommendedTopics: TopicPreference[];
  customTopics: TopicPreference[];
  isDirty: boolean;
  isLoadingTopics: boolean;
  isLoadingSuggestions: boolean;
  guid?: string;
}

export interface WrappedTopicPreferences {
  topicPreferences: TopicPreferencesState;
}

export const initialState: TopicPreferencesState = {
  ...Common.emptyState,
  memberGuid: "",
  topics: [],
  recommendedTopics: [],
  customTopics: [],
  isDirty: false,
  isLoadingTopics: false,
  isLoadingSuggestions: false,
};

interface TopicPreferenceAction extends Common.SetGuidAction {
  type: string;
  id?: string;
  payload?: any;
  query?: any;
  topicNames?: string[];
}

export const reducer = (state: TopicPreferencesState = initialState, action: TopicPreferenceAction) => {
  switch (action.type) {
    // Topic preference toggle
    case Constants.UPDATE_TOPIC_PREFERENCE:
      // Update the specific array that contains the topic ID and clear newly added flag
      const updatedTopics = state.topics.map(topic => 
        topic.id === action.id ? { ...topic, isActive: !topic.isActive, isNewlyAdded: false } : topic
      );
      
      const updatedRecommendedTopics = state.recommendedTopics.map(topic => 
        topic.id === action.id ? { ...topic, isActive: !topic.isActive } : topic
      );
      
      const updatedCustomTopics = state.customTopics.map(topic => 
        topic.id === action.id ? { ...topic, isActive: !topic.isActive } : topic
      );
      
      return {
        ...state,
        topics: updatedTopics,
        recommendedTopics: updatedRecommendedTopics,
        customTopics: updatedCustomTopics,
        isDirty: true,
      };
    
    // Add custom topics
    case Constants.ADD_CUSTOM_TOPICS:
      if (!action.topicNames || action.topicNames.length === 0) {
        return state;
      }
      
      // Create new topics with unique IDs, checking for duplicates across all topics
      const allExistingTopicNames = [
        ...state.topics.map(t => t.name.toLowerCase()),
        ...state.recommendedTopics.map(t => t.name.toLowerCase()),
        ...state.customTopics.map(t => t.name.toLowerCase())
      ];
      
      const newCustomTopics = action.topicNames
        .filter(name => name.trim() && !allExistingTopicNames.includes(name.toLowerCase()))
        .map((name, index) => ({
          id: `custom-${Date.now()}-${index}`,
          name: name.trim(),
          isActive: true,
          weight: 10, // New custom topics start as active with weight 10
        }));
      
      if (newCustomTopics.length === 0) {
        return state; // No new topics to add
      }
      
      // Add new topics to the top
      return {
        ...state,
        customTopics: [...newCustomTopics, ...state.customTopics],
        isDirty: true,
      };
    
    // User topics API
    case Request.requestConstant(Constants.FETCH_USER_TOPICS):
      return {
        ...state,
        isLoadingTopics: true,
      };
    
    case Request.successConstant(Constants.FETCH_USER_TOPICS):
      // Convert API response to our internal format with user prefix
      // User topics: use weight to determine if active AND filter out negative weights (toggled off topics)
      const userTopicsResponse = action.payload?.response;
      const userTopics = userTopicsResponse ? convertApiResponseToTopics(userTopicsResponse, [], 'user', true, false, true) : [];
      
      // Sort by weight (highest first) - isActive is already set based on weight
      const sortedUserTopics = userTopics
        .sort((a, b) => (b.weight || 0) - (a.weight || 0));
      
      return {
        ...state,
        isLoadingTopics: false,
        topics: sortedUserTopics,
      };
    case Request.successConstant(CommonConstants.FETCH_CONFIGURATION):
      return {
        ...state,
        ...Utils.initializedState(action.payload.response),
      }
    case Request.errorConstant(Constants.FETCH_USER_TOPICS):
      return {
        ...state,
        isLoadingTopics: false,
        errored: true,
        errorMessage: 'Failed to load your topics. Please try again.',
      };
    
    // Suggested topics API
    case Request.requestConstant(Constants.FETCH_SUGGESTED_TOPICS):
      return {
        ...state,
        isLoadingSuggestions: true,
      };
    
    case Request.successConstant(Constants.FETCH_SUGGESTED_TOPICS):
      // Convert API response to our internal format with suggested prefix
      // Suggested topics: ignore weight for active state, default to unchecked (false)
      const suggestedTopicsResponse = action.payload?.response;
      const suggestedTopics = suggestedTopicsResponse ? convertApiResponseToTopics(suggestedTopicsResponse, [], 'suggested', false, false) : [];
      
      // Sort by weight (highest first) - isActive is set to false for all suggested topics
      const sortedSuggestedTopics = suggestedTopics
        .sort((a, b) => (b.weight || 0) - (a.weight || 0));
      
      return {
        ...state,
        isLoadingSuggestions: false,
        recommendedTopics: sortedSuggestedTopics,
      };
    
    case Request.errorConstant(Constants.FETCH_SUGGESTED_TOPICS):
      return {
        ...state,
        isLoadingSuggestions: false,
        errored: true,
        errorMessage: 'Failed to load topic suggestions. Please try again.',
      };
    
    // Submit preferences
    case Request.requestConstant(Constants.SUBMIT_TOPIC_PREFERENCES):
      return Common.submittedState(state);
    
    case Request.successConstant(Constants.SUBMIT_TOPIC_PREFERENCES):
      // Only keep user topics that are still active (not toggled off) and clear newly added flags
      const activeUserTopics = state.topics
        .filter(topic => topic.isActive)
        .map(topic => ({ ...topic, isNewlyAdded: false }));
      
      // Move selected custom topics and recommended topics to user topics, mark as newly added
      const selectedCustomTopics = state.customTopics
        .filter(topic => topic.isActive)
        .map(topic => ({ ...topic, isNewlyAdded: true }));
      const selectedRecommendedTopics = state.recommendedTopics
        .filter(topic => topic.isActive)
        .map(topic => ({ ...topic, isNewlyAdded: true }));
      
      // Remove selected topics from custom and recommended arrays
      const remainingCustomTopics = state.customTopics.filter(topic => !topic.isActive);
      const remainingRecommendedTopics = state.recommendedTopics.filter(topic => !topic.isActive);
      
      return {
        ...state,
        ...Common.returnedState(state),
        isDirty: false,
        // User topics = existing topics (no longer newly added) + newly moved topics (marked as newly added)
        topics: [...activeUserTopics, ...selectedCustomTopics, ...selectedRecommendedTopics],
        // Keep only unselected topics in their respective arrays
        customTopics: remainingCustomTopics,
        recommendedTopics: remainingRecommendedTopics,
      };
    
    case Request.errorConstant(Constants.SUBMIT_TOPIC_PREFERENCES):
      return {
        ...state,
        ...Common.returnedState(state),
        errored: true,
        errorMessage: 'Failed to save your preferences. Please try again.',
      };
    
    case Common.SET_GUID:
      return {
        ...state,
        ...Common.setMemberGuidInState(action.guid, state)
      };
    
    // Initialize from URL params
    case Constants.INITIALIZE_FROM_URL:
      return {
        ...state,
        guid: action.query?.guid,
        identifier: action.query?.community_identifier,
      };
    
    default:
      return state;
  }
};